import { FormikErrors, FormikTouched } from 'formik';
import * as yup from 'yup';
import { WIRE_FORMAT_PATTERN } from '../../offerTimeZoneConversion';

// The prepared file picked for a new upload — no crop step, rides the multipart submit as-is.
export interface OfferPopupImageFile {
  file: Blob
  fileName: string
}

const ALLOWED_POPUP_IMAGE_TYPES = ['image/jpeg', 'image/png'];
const MAX_POPUP_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;

// Checked before the file ever enters form state -- `accept` only filters the picker dialog.
export function validatePopupImageFile(file: File): string {
  if (!ALLOWED_POPUP_IMAGE_TYPES.includes(file.type)) {
    return 'Only JPEG and PNG images are accepted.';
  }
  if (file.size > MAX_POPUP_IMAGE_SIZE_BYTES) {
    return 'Image must be 5 MB or smaller.';
  }
  return '';
}

export interface OfferPositionFormValues {
  episode_uuid: string
  node_name: string
  step_position: string
}

// The three mutually-exclusive scheduling modes, a closed and authoritative declaration:
// the form sends this field explicitly and never re-derives it from which of start_time/end_time/
// new_player_window_hours happen to be populated — a derived mode can't tell a deliberate
// open-ended offer from a forgotten end date.
export type SchedulingType = 'everyone' | 'new_users' | 'open_ended';

export interface OfferFormValues {
  identifier: string
  display_name: string
  positions: OfferPositionFormValues[]
  product_slug: string
  scheduling_type: SchedulingType
  start_time: string
  end_time: string
  active: boolean
  popup_image_url: string
  newPopupImage?: OfferPopupImageFile
  appearance_top: string
  appearance_width: string
  appearance_height: string
  new_player_window_hours: string
}

export type TopLevelField = 'identifier' | 'display_name' | 'product_slug' | 'popup_image_url'
  | 'appearance_top' | 'appearance_width' | 'appearance_height';

export const emptyOfferPosition: OfferPositionFormValues = {
  episode_uuid: '',
  node_name: '',
  step_position: '',
};

export const emptyOfferFormValues: OfferFormValues = {
  identifier: '',
  display_name: '',
  positions: [{ ...emptyOfferPosition }],
  product_slug: '',
  scheduling_type: 'everyone',
  start_time: '',
  end_time: '',
  active: false,
  popup_image_url: '',
  appearance_top: '',
  appearance_width: '',
  appearance_height: '',
  new_player_window_hours: '',
};

// The field(s) that don't belong to the newly-picked type are cleared, not just hidden — a
// start_time, end_time or new_player_window_hours left over from a previous type must never ride
// to save; the write path refuses the contradiction, but the operator should never see it fire
// from a leftover value they can no longer see on screen.
export function clearFieldsForSchedulingMode(values: OfferFormValues, schedulingType: SchedulingType): OfferFormValues {
  return {
    ...values,
    scheduling_type: schedulingType,
    start_time: schedulingType === 'new_users' ? '' : values.start_time,
    end_time: schedulingType === 'everyone' ? values.end_time : '',
    new_player_window_hours: schedulingType === 'new_users' ? values.new_player_window_hours : '',
  };
}

function isValidDateTimeFormat(value: string | undefined) {
  return Boolean(value) && WIRE_FORMAT_PATTERN.test(value as string) && !Number.isNaN(Date.parse(value as string));
}

function isPercentageString(value: string): boolean {
  return /^\d+(\.\d+)?$/.test(value) && Number(value) >= 0 && Number(value) <= 100;
}

function percentageOrEmptySchema(label: string) {
  return yup.string()
    .test('is-percentage-or-empty', `${label} must be a number between 0 and 100.`, (value) => !value || isPercentageString(value));
}

const positionSchema = yup.object({
  episode_uuid: yup.string().required('Episode identifier is required.'),
  node_name: yup.string().required('Node name is required.'),
  step_position: yup.string().required('Step position is required.')
    .test('is-whole-number', 'Step position must be a whole number of 0 or greater.', (value) => Boolean(value) && /^\d+$/.test(value as string)),
});

// Both directions, mirroring the write path exactly: the field the type requires is validated as
// required; every field the type forbids is validated as empty. A schema that only checked the
// required side would let the operator submit a contradiction the server then has to catch instead.
export const offerFormSchema = yup.object({
  display_name: yup.string().required('Display name is required.'),
  identifier: yup.string().required('Identifier is required.').matches(/^[a-z0-9-]+$/, 'Identifier may only contain lowercase letters, digits and hyphens.'),
  positions: yup.array().of(positionSchema).min(1, 'At least one story position is required.'),
  product_slug: yup.string().required('Product identifier is required.'),
  start_time: yup.string().when('scheduling_type', (schedulingType: SchedulingType, schema: yup.StringSchema) => (
    schedulingType === 'new_users'
      ? schema.test('is-empty-for-new-users', 'A "for new users" offer cannot have a start date.', (value) => !value)
      : schema.required('Start date/time is required.')
        .test('is-valid-format', 'Start date/time must be a valid date. If set, End date/time must also be valid.', isValidDateTimeFormat)
  )),
  end_time: yup.string().when('scheduling_type', (schedulingType: SchedulingType, schema: yup.StringSchema) => (
    schedulingType === 'everyone'
      ? schema.required('End date/time is required.')
        .test('is-valid-format', 'Start date/time must be a valid date. If set, End date/time must also be valid.', isValidDateTimeFormat)
        .test('is-after-start', 'End date/time must be after the start date/time.', (value, context) => (
          !value || !isValidDateTimeFormat(context.parent.start_time)
            || Date.parse(value) > Date.parse(context.parent.start_time)
        ))
      : schema.test('is-empty-unless-everyone', 'Only a "for everyone" offer can have an end date.', (value) => !value)
  )),
  popup_image_url: yup.string().required('Popup image is required.'),
  appearance_top: percentageOrEmptySchema('Vertical position'),
  appearance_width: percentageOrEmptySchema('Width'),
  appearance_height: percentageOrEmptySchema('Height'),
  new_player_window_hours: yup.string().when('scheduling_type', (schedulingType: SchedulingType, schema: yup.StringSchema) => (
    schedulingType === 'new_users'
      ? schema.required('New-player window is required.')
        .test('is-positive-whole-number', 'New-player window must be a positive whole number.', (value) => Boolean(value) && /^[1-9]\d*$/.test(value as string))
      : schema.test('is-empty-unless-new-users', 'Only a "for new users" offer can have a new-player window.', (value) => !value)
  )),
});

export interface StoredOfferPosition {
  episode_uuid?: string
  step_position?: number
  node_name?: string | null
}

export interface StoredOffer {
  id?: number
  identifier?: string
  display_name?: string
  product_slug?: string
  scheduling_type?: SchedulingType
  active?: boolean
  start_time?: string
  end_time?: string
  new_player_window_hours?: number | null
  appearance?: string | Record<string, string | number>
  positions?: StoredOfferPosition[]
}

export function offerToFormValues(offer: StoredOffer): OfferFormValues {
  const storedPositions = offer.positions && offer.positions.length > 0 ? offer.positions : [{}];
  const appearance = typeof offer.appearance === 'string' ? JSON.parse(offer.appearance) : (offer.appearance || {});
  return {
    identifier: offer.identifier ?? '',
    display_name: offer.display_name ?? '',
    positions: storedPositions.map((position) => ({
      episode_uuid: position.episode_uuid ?? '',
      node_name: position.node_name ?? '',
      step_position: position.step_position != null ? String(position.step_position) : '',
    })),
    product_slug: offer.product_slug ?? '',
    scheduling_type: offer.scheduling_type ?? 'everyone',
    start_time: offer.start_time ?? '',
    end_time: offer.end_time ?? '',
    active: Boolean(offer.active),
    popup_image_url: appearance.popup_image_url ?? '',
    appearance_top: appearance.top != null ? String(appearance.top) : '',
    appearance_width: appearance.width != null ? String(appearance.width) : '',
    appearance_height: appearance.height != null ? String(appearance.height) : '',
    new_player_window_hours: offer.new_player_window_hours != null ? String(offer.new_player_window_hours) : '',
  };
}

export function buildOfferPayload(values: OfferFormValues) {
  return {
    identifier: values.identifier,
    display_name: values.display_name,
    product_slug: values.product_slug,
    scheduling_type: values.scheduling_type,
    active: values.active,
    start_time: values.start_time || null,
    end_time: values.end_time || null,
    new_player_window_hours: values.new_player_window_hours || null,
    appearance: {
      popup_image_url: values.popup_image_url,
      ...(values.appearance_top ? { top: Number(values.appearance_top) } : {}),
      ...(values.appearance_width ? { width: Number(values.appearance_width) } : {}),
      ...(values.appearance_height ? { height: Number(values.appearance_height) } : {}),
    },
    positions: values.positions.map((position) => ({
      episode_uuid: position.episode_uuid,
      node_name: position.node_name,
      step_position: Number(position.step_position),
    })),
  };
}

// multipart/form-data is what carries the popup image blob (Events' own createFormDataFromFormValues
// pattern); positions/appearance travel as JSON text inside it since form fields have no
// structured type of their own.
export function createFormDataFromOfferFormValues(values: OfferFormValues): FormData {
  const payload = buildOfferPayload(values);
  const formData = new FormData();
  formData.append('identifier', payload.identifier);
  formData.append('display_name', payload.display_name);
  formData.append('product_slug', payload.product_slug);
  formData.append('scheduling_type', payload.scheduling_type);
  formData.append('active', String(payload.active));
  if (payload.start_time) {
    formData.append('start_time', payload.start_time);
  }
  if (payload.end_time) {
    formData.append('end_time', payload.end_time);
  }
  if (payload.new_player_window_hours != null) {
    formData.append('new_player_window_hours', String(payload.new_player_window_hours));
  }
  formData.append('appearance', JSON.stringify(payload.appearance));
  formData.append('positions', JSON.stringify(payload.positions));
  if (values.newPopupImage) {
    formData.append('image', values.newPopupImage.file, values.newPopupImage.fileName);
  }
  return formData;
}

export interface FieldRejection {
  scope: 'top' | 'position'
  field: string
  message: string
  matchNodeName?: string
  matchStepPosition?: number
}

const APPEARANCE_LABEL_TO_FIELD: Record<string, TopLevelField> = {
  'Popup image': 'popup_image_url',
  'Vertical position': 'appearance_top',
  Width: 'appearance_width',
  Height: 'appearance_height',
};

// Parses the server's banner text back into which field (and which position row, for a
// multi-position offer) it is about, and the shorter field-level copy for that field.
export function deriveFieldRejection(bannerMessage: string): FieldRejection {
  const markupMatch = bannerMessage.match(/^(.+?) must be plain text\. Remove any formatting or HTML tags/);
  if (markupMatch) {
    const field = APPEARANCE_LABEL_TO_FIELD[markupMatch[1]];
    if (field) {
      return { scope: 'top', field, message: `${markupMatch[1]} must be plain text — remove any formatting or HTML tags.` };
    }
  }
  const percentageMatch = bannerMessage.match(/^(.+?) must be a number between 0 and 100\./);
  if (percentageMatch) {
    const field = APPEARANCE_LABEL_TO_FIELD[percentageMatch[1]];
    if (field) {
      return { scope: 'top', field, message: bannerMessage };
    }
  }
  const productMatch = bannerMessage.match(/^Product "(.*)" was not found in the catalogue\./);
  if (productMatch) {
    return { scope: 'top', field: 'product_slug', message: productMatch[0] };
  }
  const identifierMatch = bannerMessage.match(/^Identifier ".*" is already used by offer "(.*)"\./);
  if (identifierMatch) {
    return { scope: 'top', field: 'identifier', message: `This identifier is already taken by "${identifierMatch[1]}".` };
  }
  if (/^Identifier must be \d+ characters or fewer\./.test(bannerMessage)) {
    return { scope: 'top', field: 'identifier', message: bannerMessage };
  }
  if (/^Display name must be \d+ characters or fewer\./.test(bannerMessage)) {
    return { scope: 'top', field: 'display_name', message: bannerMessage };
  }
  const positionTooLongLabels: Record<string, keyof OfferPositionFormValues> = {
    'Episode identifier': 'episode_uuid', 'Node name': 'node_name',
  };
  const positionTooLongMatch = bannerMessage.match(
    /^(Episode identifier|Node name) must be \d+ characters or fewer\. Shorten it and try again\./,
  );
  if (positionTooLongMatch) {
    return { scope: 'position', field: positionTooLongLabels[positionTooLongMatch[1]], message: bannerMessage };
  }
  const stepOutOfRangeMatch = bannerMessage.match(/^Step position must be between 0 and \d+\./);
  if (stepOutOfRangeMatch) {
    return { scope: 'position', field: 'step_position', message: bannerMessage };
  }
  const conflictMatch = bannerMessage.match(/^Step (\d+) in node "(.*?)" is already held by live offer "(.*?)"\./);
  if (conflictMatch) {
    return {
      scope: 'position',
      field: 'step_position',
      message: `Step ${conflictMatch[1]} in node "${conflictMatch[2]}" is already held by live offer "${conflictMatch[3]}".`,
      matchNodeName: conflictMatch[2],
      matchStepPosition: Number(conflictMatch[1]),
    };
  }
  return { scope: 'top', field: '', message: bannerMessage };
}

// Which position row (if any) a position-conflict rejection is about — matched by what the
// operator typed. The banner carries no episode, so two of the operator's OWN rows can
// coincidentally share a name and step across different episodes; when the match is not unique,
// no row is highlighted rather than guessing — the banner still shows either way.
export function findRejectedPositionIndex(
  positions: OfferPositionFormValues[],
  rejection: FieldRejection | null,
): number {
  if (rejection?.scope !== 'position') {
    return -1;
  }
  // A too-long rejection carries no identifying value to match by (the coordinate itself is what
  // overflowed) — with exactly one row there is nothing else it could be about; with more than one
  // there is no way to tell which, so no row is highlighted rather than guessing.
  if (rejection.matchNodeName === undefined && rejection.matchStepPosition === undefined) {
    return positions.length === 1 ? 0 : -1;
  }
  const matches = positions.reduce<number[]>((indices, position, index) => {
    const isMatch = position.node_name === rejection.matchNodeName && Number(position.step_position) === rejection.matchStepPosition;
    return isMatch ? [...indices, index] : indices;
  }, []);
  return matches.length === 1 ? matches[0] : -1;
}

export function topLevelFieldError(
  field: TopLevelField,
  touched: FormikTouched<OfferFormValues>,
  errors: FormikErrors<OfferFormValues>,
  rejection: FieldRejection | null,
): string {
  const validationError = touched[field] ? errors[field] : '';
  const rejectionMessage = rejection?.scope === 'top' && rejection.field === field ? rejection.message : '';
  return (validationError as string) || rejectionMessage || '';
}

export function positionFieldError(
  index: number,
  field: keyof OfferPositionFormValues,
  touched: FormikTouched<OfferFormValues>,
  errors: FormikErrors<OfferFormValues>,
  rejection: FieldRejection | null,
  rejectedPositionIndex: number,
): string {
  const row = Array.isArray(touched.positions) ? touched.positions[index] : undefined;
  const rowTouched = row ? (row as Partial<Record<keyof OfferPositionFormValues, boolean>>)[field] : false;
  const errorRow = Array.isArray(errors.positions) ? errors.positions[index] : undefined;
  const rowValidationError = rowTouched && errorRow
    ? (errorRow as Partial<Record<keyof OfferPositionFormValues, string>>)[field]
    : '';
  const rejectionMessage = rejection?.scope === 'position' && rejection.field === field && index === rejectedPositionIndex
    ? rejection.message
    : '';
  return rowValidationError || rejectionMessage || '';
}
